import { Component, OnInit } from '@angular/core';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { FinancialResults, FinancialSnapshot, FinancialSummary } from '../../models/financial-results';
import { FinancialService } from '../../services/financial.service';

@Component({
    selector: 'app-financial',
    standalone: true,
    imports: [CommonModule, FormsModule, TableModule, DatePickerModule, TooltipModule, DatePipe, DecimalPipe],
    templateUrl: './financial.component.html',
    styleUrls: ['./financial.component.scss'],
})
export class FinancialComponent implements OnInit {
    rows: FinancialResults[] = [];
    summary: FinancialSummary = this.getEmptySummary();
    snapshot: FinancialSnapshot = this.getEmptySnapshot();
    isLoading = false;
    errorMessage = '';
    since!: Date;
    until!: Date;
    formulas = {
        cash_in: 'Caixa recebido = soma de Transaction.value das compras de moedas aprovadas (COIN_PURCHASE) no dia.',
        realized_revenue: 'Receita realizada = Take Rate CS + Take Rate LoL + Passe + Loja + Missões extras.',
        net_realized_result: 'Resultado realizado = Receita realizada - Saques pagos - Saídas de missões extras.',
        take_rate_cs: 'Take Rate CS = moedas consumidas em batalhas de CS * COIN_VALUE * 20%.',
        take_rate_lol: 'Take Rate LoL = moedas consumidas em batalhas de LoL * COIN_VALUE * 20%.',
        battle_pass_income: 'Passe = compras aprovadas de Battle Pass convertidas para R$ por COIN_VALUE ou GEM_VALUE.',
        store_income: 'Loja = compras aprovadas de skins/heróis convertidas para R$ por COIN_VALUE ou GEM_VALUE.',
        extra_missions_income: 'Missões extras = moedas apostadas em missões aprovadas (MISSION_STAKE) * COIN_VALUE.',
        extra_missions_outflow: 'Saídas missões extras = gemas pagas em recompensas de missões (MISSION_REWARD) * GEM_VALUE.',
        cash_out: 'Saques pagos = soma de Transaction.value dos saques aprovados (CASH_OUT) no dia.',
    };

    constructor(
        private financialService: FinancialService,
        private route: ActivatedRoute,
        private router: Router
    ) {
        const { since, until } = this.getDefaultRange();
        this.applyRange(since, until);
    }

    ngOnInit(): void {
        this.route.queryParams.subscribe((params) => {
            if (params['since'] || params['until']) {
                const { since: defaultSince, until: defaultUntil } = this.getDefaultRange();
                const since = params['since'] ? this.parseDate(params['since']) : defaultSince;
                const until = params['until'] ? this.parseDate(params['until']) : defaultUntil;
                this.applyRange(since, until);
            } else {
                const { since, until } = this.getDefaultRange();
                this.applyRange(since, until);
            }

            this.fetchResults();
        });
    }

    private getDefaultRange(): { since: Date; until: Date } {
        const until = this.startOfDay(new Date());
        const since = this.addDays(until, -29);
        return { since, until };
    }

    private getEmptySummary(): FinancialSummary {
        return {
            cash_in: 0,
            take_rate_cs: 0,
            take_rate_lol: 0,
            battle_pass_income: 0,
            store_income: 0,
            extra_missions_income: 0,
            extra_missions_outflow: 0,
            cash_out: 0,
            realized_revenue: 0,
            net_realized_result: 0,
            capture_margin: 0,
        };
    }

    private getEmptySnapshot(): FinancialSnapshot {
        return {
            coin_custody_value: 0,
            gem_withdrawable_value: 0,
            pending_cash_out_value: 0,
            pending_cash_out_count: 0,
            estimated_net_platform_cash: 0,
            blacklisted_accounts_count: 0,
        };
    }

    private startOfDay(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    private addDays(date: Date, days: number): Date {
        const result = new Date(date);
        result.setDate(result.getDate() + days);
        return this.startOfDay(result);
    }

    private parseDate(value: string): Date {
        const [year, month, day] = value.split('-').map(Number);
        if (!year || !month || !day) {
            return this.startOfDay(new Date());
        }
        return new Date(year, month - 1, day);
    }

    private formatDateForApi(date: Date): string {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private applyRange(since: Date, until: Date): void {
        const normalizedSince = this.startOfDay(since);
        const normalizedUntil = this.startOfDay(until);
        this.since = normalizedSince <= normalizedUntil ? normalizedSince : normalizedUntil;
        this.until = normalizedSince <= normalizedUntil ? normalizedUntil : normalizedSince;
    }

    applyDateFilter(): void {
        const { since: defaultSince, until: defaultUntil } = this.getDefaultRange();
        const since = this.since ? this.startOfDay(this.since) : defaultSince;
        const until = this.until ? this.startOfDay(this.until) : defaultUntil;
        this.applyRange(since, until);
        this.updateQueryParams();
    }

    clearFiltersAndRefresh(): void {
        this.fetchResults();
    }

    exportToCsv(): void {
        if (!this.rows.length) {
            return;
        }

        const headers = [
            'Data',
            'Caixa Recebido (R$)',
            'Receita Realizada (R$)',
            'Resultado Realizado (R$)',
            'Take Rate CS (R$)',
            'Take Rate LoL (R$)',
            'Passe (R$)',
            'Loja (R$)',
            'Missões extras (R$)',
            'Saídas missões extras (R$)',
            'Saques Pagos (R$)',
        ];

        const formatNumber = (value: number | null | undefined): string =>
            Number(value || 0).toFixed(2).replace('.', ',');

        const csvRows = this.rows.map((row) => [
            this.formatDateForDisplay(row.date),
            formatNumber(row.cash_in),
            formatNumber(row.realized_revenue),
            formatNumber(row.net_realized_result),
            formatNumber(row.take_rate_cs),
            formatNumber(row.take_rate_lol),
            formatNumber(row.battle_pass_income),
            formatNumber(row.store_income),
            formatNumber(row.extra_missions_income),
            formatNumber(row.extra_missions_outflow),
            formatNumber(row.cash_out),
        ]);

        const csv = [headers, ...csvRows]
            .map((line) => line.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(';'))
            .join('\n');

        const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const since = this.formatDateForApi(this.since);
        const until = this.formatDateForApi(this.until);

        link.href = url;
        link.download = `financeiro_${since}_a_${until}.csv`;
        link.click();

        URL.revokeObjectURL(url);
    }

    private updateQueryParams(): void {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                since: this.formatDateForApi(this.since),
                until: this.formatDateForApi(this.until),
            },
        });
    }

    fetchResults(): void {
        this.isLoading = true;
        this.errorMessage = '';
        const { queryParamMap } = this.route.snapshot;
        const since = queryParamMap.get('since');
        const until = queryParamMap.get('until');
        const params: { since?: string; until?: string } = {};

        if (since) {
            params.since = since;
        }
        if (until) {
            params.until = until;
        }

        this.financialService.getFinancialResults(params).subscribe({
            next: (response) => {
                this.summary = this.normalizeSummary(response?.summary);
                this.snapshot = this.normalizeSnapshot(response?.snapshot);
                this.rows = (response?.results ?? []).map((item) => ({
                    ...item,
                    cash_in: Number(item.cash_in || 0),
                    take_rate_cs: Number(item.take_rate_cs || 0),
                    take_rate_lol: Number(item.take_rate_lol || 0),
                    battle_pass_income: Number(item.battle_pass_income || 0),
                    store_income: Number(item.store_income || 0),
                    extra_missions_income: Number(item.extra_missions_income || 0),
                    extra_missions_outflow: Number(item.extra_missions_outflow || 0),
                    cash_out: Number(item.cash_out || 0),
                    realized_revenue: Number(item.realized_revenue || 0),
                    net_realized_result: Number(item.net_realized_result || 0),
                }));
                this.isLoading = false;
            },
            error: () => {
                this.rows = [];
                this.summary = this.getEmptySummary();
                this.snapshot = this.getEmptySnapshot();
                this.errorMessage = 'Não foi possível carregar os resultados financeiros.';
                this.isLoading = false;
            },
        });
    }

    private normalizeSummary(summary?: FinancialSummary): FinancialSummary {
        return {
            cash_in: Number(summary?.cash_in || 0),
            take_rate_cs: Number(summary?.take_rate_cs || 0),
            take_rate_lol: Number(summary?.take_rate_lol || 0),
            battle_pass_income: Number(summary?.battle_pass_income || 0),
            store_income: Number(summary?.store_income || 0),
            extra_missions_income: Number(summary?.extra_missions_income || 0),
            extra_missions_outflow: Number(summary?.extra_missions_outflow || 0),
            cash_out: Number(summary?.cash_out || 0),
            realized_revenue: Number(summary?.realized_revenue || 0),
            net_realized_result: Number(summary?.net_realized_result || 0),
            capture_margin: Number(summary?.capture_margin || 0),
        };
    }

    private normalizeSnapshot(snapshot?: FinancialSnapshot): FinancialSnapshot {
        return {
            coin_custody_value: Number(snapshot?.coin_custody_value || 0),
            gem_withdrawable_value: Number(snapshot?.gem_withdrawable_value || 0),
            pending_cash_out_value: Number(snapshot?.pending_cash_out_value || 0),
            pending_cash_out_count: Number(snapshot?.pending_cash_out_count || 0),
            estimated_net_platform_cash: Number(snapshot?.estimated_net_platform_cash || 0),
            blacklisted_accounts_count: Number(snapshot?.blacklisted_accounts_count || 0),
        };
    }

    private formatDateForDisplay(value: string): string {
        const [year, month, day] = value.split('-');
        if (!year || !month || !day) {
            return value;
        }
        return `${day}/${month}/${year}`;
    }
}
